<template>
  <TextBubble
    :style="annotationStyle"
    :id="`annotation-${annotation._id}`"
    ref="annotationEl"
    :class="textBubbleClasses"
    :emphasize="isEmphasized"
    :disabled="isDisabled"
    :border-color="vertical ? (roundBorderColor === 'blue' ? '#7cb6c9' : '#e49141') : undefined"
    @keydown="handleInputKeyDown"
    @click="handleClickAnnotation"
    @mouseleave="hoverState.doHoverOutAnnotation"
    @hoverIn="handleHoverIn"
    @hoverOut="handleHoverOut"
  >
    <!-- Toolbar -->
    <template #top v-if="showToolbar">
      <!-- Citation icon dummy (when not hovering) -->
      <div
        v-if="hasCitations && !hoverState.hover.value"
        class="citation-icon-dummy uk-flex uk-flex-column"
      >
        <img
          :src="`/icons/black/icon-black${previousCitation ? '-close' : '-open'}-citation.svg`"
          width="25"
          style="height: 24px"
          :class="{ 'open-citation-style': !previousCitation }"
        />
      </div>

      <AnnotationToolbar
        :publicView="publicView"
        :isInstruction="isInstruction"
        :isHostInstructionMode="isHostInstructionMode"
        :isTeamLeadOrHostMode="isTeamLeadOrHostMode"
        :showCounterpointSelect="showCounterpointSelect"
        :annotationType="annotation.type"
        :permissionLevel="permissionLevel"
        :isCurrentAuthor="isCurrentAuthor"
        :isCurrentRound="isCurrentRound"
        :showApprovalButtons="showApprovalButtons"
        :authorName="authorName"
        :teamRepliesCount="annotation.teamReplies?.length || 0"
        :hasSelectedReply="!!annotation.selectedReply"
        :canUpdateAndDelete="canUpdateAndDelete"
        :roundPublished="roundPublished(parentCounterpoint.round)"
        :isTeamModeActive="isTeamModeActive"
        :myLatestRoundNumber="myLatestRoundNumber"
        :canDelete="canDeleteAnnotation"
        :deleteLabel="`Delete ${annotationTypeLabel(annotation)}`"
        :hasCitations="hasCitations"
        :citationOpen="previousCitation"
        :citationDisabled="citationDis"
        :hover="hoverState.hover.value"
        :roundStatus="roundStatus"
        :annotationText="editingState.annotationText.value"
        :parentCounterpointId="parentCounterpoint._id"
        :currentTeamLatestRoundCounterpointsId="currentTeamLatestRoundCounterpoints?._id"
        :isGroupRoundUser="isGroupRoundUser"
        :vertical="vertical"
        :isCohortTeam="isCohortTeam"
        @repliesClick="doRepliesClick"
        @switchType="editingState.handleAnnotationSwitch"
        @showThreadsView="showVerticalView"
        @delete="handleDelete"
        @toggleCitation="showPreviousCitation"
        @addCitation="handleAddCitation"
      />
    </template>

    <!-- Main content -->
    <template #default>
      <!-- Instruction text (if applicable) -->
      <div
        v-if="!publicView && !useInstruction && !showCounterpointSelect"
        v-html="annotation.instruction"
        class="uk-text-small uk-text-italic uk-text-muted uk-background-muted uk-border-rounded"
      ></div>

      <!-- Static mode (read-only paragraphs) -->
      <template
        v-if="
          (isStaticMode ||
            annotation.canUpdateOrDelete === false ||
            (!showSaveButton && !editingState.editMode.value)) &&
          !editingState.editMode.value
        "
      >
        <div class="paragraphs" v-if="annotationParagraphs.length > 0">
          <AnnotatedParagraph
            :removeMargin="true"
            v-for="(p, idx) in annotationParagraphs"
            :key="`P${idx}`"
            :ref="`P${idx}`"
            :paragraph="p"
            :node-annotations="paragraphNodeAnnotations"
            element-id-prefix="annotation"
            :annHovered="hoverState.hover.value"
            :geometry-terminal-element-id="`scroller_cp_${parentCounterpoint._id}`"
            @geometriesUpdated="handleGeometriesUpdated"
            @mouseClickNode="doMarkupClick"
            @hoverIn="hoverState.doHoverInAnnotation"
          />
        </div>
        <div
          v-else
          class="simple-comment"
          v-html="annotation.comment"
          style="padding: 10px 0"
        ></div>
      </template>

      <!-- Editable mode (or view mode when no paragraphs available) -->
      <div v-else>
        <TipTap
          ref="mainAnnotation"
          v-model="editingState.annotationText.value"
          :disabled="isMainAnnotationDisabled && !isGroupRoundUser"
          :max-characters="annotationCharacterLimit || defaultCharacterLimit"
          :style="tiptapStyle"
          :class="{
            'uk-hidden': showCounterpointSelect,
            'tip-tap-overflow-ellipsis': hoverState.addEllipsis.value,
          }"
          @focusIn="handleFocusIn"
          @focusOut="editingState.editMode.value ? null : handleFocusOut()"
          @editorCreated="handleGeometryUpdate"
          @updateModelValue="handleGeometryUpdate"
        />

        <TipTap
          v-model="teamAnnotationText"
          :class="{ 'uk-hidden': !showCounterpointSelect }"
          :style="tiptapStyle"
          @editorCreated="handleGeometryUpdate"
          @updateModelValue="handleGeometryUpdate"
        />
      </div>

      <!-- Arrows (disabled in vertical/threads view) -->
      <div class="arrows" v-if="!vertical">
        <template v-if="counterpointLoaded">
          <AnnotationArrow
            v-for="ann in refAnnotations"
            :key="`arr${ann._id}`"
            :annotation="ann"
            :arrow-type="hasSelectedText(ann) ? 'inline' : 'block'"
            :ref="`annotation-arrow-${ann._id}`"
            type="ann"
            @click="handleArrowClick"
          />
        </template>
      </div>

      <!-- Team reply input -->
      <hr v-if="showTeamReplyInputBox" />
      <TipTap
        ref="teamReplyEditor"
        v-if="showTeamReplyInputBox"
        v-model="editingState.teamReplyComment.value"
        :max-characters="annotationCharacterLimit"
        :disabled="
          permissionLevel === PermissionLevel.COHORT &&
          (roundStatus !== RoundStatus.DRAFTING || teamAnnotationsSubmitted)
        "
        @focusIn="handleTeamReplyFocusIn"
        @focusOut="handleTeamReplyFocusOut"
        @editorCreated="handleGeometryUpdate"
        @updateModelValue="handleGeometryUpdate"
      />

      <!-- Character count (for team selection) -->
      <div
        v-if="showCounterpointSelect"
        class="text-meta-data uk-text-right"
        :class="{ 'uk-text-danger': characterCountRemaining < 0 }"
      >
        Characters left: <span>{{ characterCountRemaining }}</span>
      </div>

      <!-- Citations list -->
      <AnnotationCitationList
        :citations="annotationCitations"
        :isOpen="previousCitation"
        :canAddCitation="canAddCitationsComputed"
        :showSaveButton="showCounterpointSelect"
        :isValid="characterCountRemaining >= 0"
        :parent="annotation"
        :parentCounterpoint="parentCounterpoint"
        :parentAuthor="annotation.author"
        @addCitation="handleAddCitation"
        @save="submitTeamReplies"
      />

      <!-- Footer (likes/dislikes for public view) -->
      <AnnotationFooter
        v-if="showFooter"
        :likeStatus="annotationPublicLikesFlag[annotation._id]?.status"
        :likesCount="annotationPublicLikesTotalsMap[annotation._id]?.likes || 0"
        :dislikesCount="annotationPublicLikesTotalsMap[annotation._id]?.dislikes || 0"
        :showCounts="showAnnotationLikes"
        :publicView="publicView"
        :reportView="reportView"
        @like="likesState.doLike"
        @dislike="likesState.doDislike"
      />

      <!-- Approval buttons -->
      <AnnotationApprovalButtons
        v-if="showApprovalButtons"
        :isApproved="isAnnotationApproved"
        :isDisapproved="isAnnotationDisapproved"
        :editMode="editingState.editMode.value"
        @approve="approvalState.doApprove"
        @restore="editingState.doRestoreText"
        @save="handleFocusOut"
      />

      <!-- Save button -->
      <div class="uk-flex uk-flex-right" :class="{ 'display-none': hoverState.displayNone.value }">
        <button
          v-if="editingState.saving.value"
          class="uk-button uk-button-primary uk-margin-top uk-border-rounded uk-button-small size-btn"
        >
          <div uk-spinner class="padding-action"></div>
        </button>
        <button
          v-else-if="editingState.errorButton.value"
          @click="doFocusOutAnn()"
          class="uk-button uk-button-danger uk-margin-top uk-border-rounded uk-button-small size-btn"
        >
          <span uk-icon="close"></span>
        </button>
        <button
          v-else-if="showSaveButton"
          @click="doFocusOutAnn()"
          class="uk-button uk-button-primary uk-margin-top uk-border-rounded uk-button-small size-btn"
        >
          <img src="/icons/white/icon-white-savedisk.svg" class="padding-action" />
        </button>
      </div>
    </template>
  </TextBubble>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, toRef, getCurrentInstance } from 'vue';
import UIkit from 'uikit';

// Components
import TextBubble from '@/components/display/general/TextBubble.vue';
import TipTap from '@/components/annotations/TipTap.vue';
import AnnotatedParagraph from '@/components/general/AnnotatedParagraph.vue';
import AnnotationArrow from '@/components/annotations/AnnotationArrow.vue';
import AnnotationToolbar from './components/AnnotationToolbar.vue';
import AnnotationFooter from './components/AnnotationFooter.vue';
import AnnotationApprovalButtons from './components/AnnotationApprovalButtons.vue';
import AnnotationCitationList from './components/AnnotationCitationList.vue';

// Composables
import { useAnnotationGeometry } from './composables/useAnnotationGeometry';
import { useAnnotationLikes } from './composables/useAnnotationLikes';
import { useAnnotationApproval } from './composables/useAnnotationApproval';
import { useAnnotationEditing } from './composables/useAnnotationEditing';
import { useAnnotationHover } from './composables/useAnnotationHover';
import { useStore } from '@/composables/useVuexHelpers';

// Types
import type { Annotation, Counterpoint } from './types/annotation.types';

// Utils
import PermissionLevel from '@/assets/js/enum/PermissionLevel';
import RoundStatus from '@/assets/js/enum/RoundStatus';
import { EventBus } from '@/assets/js/event-bus';
import { getId, dragElement } from '@/assets/js/utils';
import { xhr } from '@/xhr/endpoints';

// Props
interface Props {
  annotation: Annotation;
  teamReply?: any;
  disabled?: boolean | string;
  published?: boolean | string;
  showDeleteButton?: boolean | string;
  color?: string;
  parentCounterpoint: Counterpoint;
  vertical?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  published: false,
  showDeleteButton: false,
  color: 'white',
  vertical: false,
});

// Emits
const emit = defineEmits([
  'mainTextChanged',
  'geometryUpdated',
  'resized',
  'hoverIn',
  'hoverOut',
  'click',
  'saved',
  'geometriesUpdated',
  'markupClick',
]);

// Store
const store = useStore();
const instance = getCurrentInstance();

// Refs
const annotationEl = ref<any>(null);
const mainAnnotation = ref<any>(null);
const teamReplyEditor = ref<any>(null);

// Local state
const annotationCharacterLimit = ref(200);
const previousCitation = ref(false);
const citationDis = ref(true);
const teamAnnotationText = ref('');
const bumpEffect = ref(false);
const canUpdateAndDelete = ref(true);
const activeAnnotations = ref<string[]>([]);

// Store getters
const userId = computed(() => store.getters.userId);
const permissionLevel = computed(() => store.getters.permissionLevel);
const myLatestRoundNumber = computed(() => store.getters.myLatestRoundNumber);
const myLatestRound = computed(() => store.getters.myLatestRound);
const roundStatus = computed(() => store.getters.roundStatus);
const annotationTypeLabel = computed(() => store.getters.annotationTypeLabel);
const teamMemberData = computed(() => store.getters.teamMemberData);
const getRefByAnnotationsIds = computed(() => store.getters.getRefByAnnotationsIds);
const hasSelectedText = computed(() => store.getters.hasSelectedText);
const teamAnnotationsSubmitted = computed(() => store.getters.teamAnnotationsSubmitted);
const roundPublished = computed(() => store.getters.roundPublished);
const roundActive = computed(() => store.getters.roundActive);
const currentTeamLatestRoundCounterpoints = computed(
  () => store.getters.currentTeamLatestRoundCounterpoints
);
const inlineNodeAnnotations = computed(() => store.getters.inlineNodeAnnotations);
const arrowsArray = computed(() => store.getters.arrowsArray);
const cachedInlineNodeAnnotations = computed(() => store.state.cachedInlineNodeAnnotations);
const roundCount = computed(() => store.getters.roundCount);
const lastRoundNumber = computed(() => store.getters.lastRoundNumber);
const refByAnnotationsMap = computed(() => store.getters.refByAnnotationsMap);
const counterpointsFlat = computed(() => store.getters.counterpointsFlat);

// Store state
const annotationHoverIds = computed(() => store.state.annotationHoverIds);
const annotationFocusIds = computed(() => store.state.annotationFocusIds);
const citations = computed(() => store.state.citations);
const counterpointLoaded = computed(() => store.state.counterpointLoaded);
const publicView = computed(() => store.state.publicView);
const reportView = computed(() => store.state.reportView);
const annotationsVisible = computed(() => store.state.annotationsVisible);
const visitor = computed(() => store.state.visitor);
const annotationPublicLikesTotalsMap = computed(() => store.state.annotationPublicLikesTotalsMap);
const annotationPublicLikesFlag = computed(() => store.state.annotationPublicLikesFlag);
const showAnnotationLikes = computed(() => store.state.showAnnotationLikes);
const defaultCharacterLimit = computed(() => store.state.defaultCharacterLimit);
const showingCounterpointSidePanel = computed(() => store.state.showingCounterpointSidePanel);
const isTeamModeActive = computed(() => store.state.isTeamModeActive);
const annotationActive = computed(() => store.state.annotationActive);
const selectedInstruction = computed(() => store.state.selectedInstruction);
const showCounterpointSelect = computed(() => store.state.showCounterpointSelect);
const documentData = computed(() => store.state.documentData);
const currentTeam = computed(() => store.state.currentTeam);

// Initialize composables
const geometryState = useAnnotationGeometry(
  toRef(props, 'annotation'),
  annotationEl,
  publicView,
  showingCounterpointSidePanel
);

const likesState = useAnnotationLikes(
  toRef(props, 'annotation'),
  annotationPublicLikesTotalsMap,
  annotationPublicLikesFlag,
  visitor,
  map => store.commit('setAnnotationPublicLikesTotalsMap', map),
  flag => store.commit('setAnnotationPublicLikesFlag', flag)
);

const approvalState = useAnnotationApproval(toRef(props, 'annotation'), params =>
  store.dispatch('saveAnnotationApproval', params)
);

const editingState = useAnnotationEditing(
  toRef(props, 'annotation'),
  toRef(props, 'parentCounterpoint'),
  params => store.dispatch('updateAnnotationText', params),
  params => store.dispatch('updateTeamReply', params),
  params => store.dispatch('switchAnnotationType', params),
  annotation => store.dispatch('annotationFocusIn', annotation),
  () => store.dispatch('annotationFocusOut'),
  getRefByAnnotationsIds.value
);

const hoverState = useAnnotationHover(
  toRef(props, 'annotation'),
  toRef(props, 'parentCounterpoint'),
  annotationActive,
  params => store.dispatch('setSelectedAnnotation', params),
  annotation => store.dispatch('annotationFocusIn', annotation),
  () => store.dispatch('annotationFocusOut'),
  getRefByAnnotationsIds.value
);

// Computed properties
const annotationStyle = computed(() => ({
  visibility: annotationVisible.value?.visible || store.getters.useExcerpts ? 'visible' : 'hidden',
  position: annotationVisible.value?.top ? 'absolute' : 'unset',
  top: annotationVisible.value?.top + 'px',
}));

const annotationVisible = computed(() => {
  if (
    annotationsVisible.value[props.annotation._id] ||
    annotationsVisible.value[props.annotation._id] !== null
  ) {
    return annotationsVisible.value[props.annotation._id];
  }
  return 0;
});

const isEmphasized = computed(() => {
  return (
    annotationHoverIds.value[props.annotation._id] || annotationFocusIds.value[props.annotation._id]
  );
});

const isDisabled = computed(() => {
  return (
    props.disabled && !publicView.value && !isGroupRoundUser.value && !showApprovalButtons.value
  );
});

const isStaticMode = computed(() => {
  const isCurrentUserCohort = permissionLevel.value === PermissionLevel.COHORT;

  const hasTeamMemberWithCohort = currentTeam.value?.members?.some(
    member =>
      (member.status === 'Accepted' || member.status === 'Invited') &&
      member.teamRole === PermissionLevel.COHORT
  );

  const isCohortTeam = isCurrentUserCohort || hasTeamMemberWithCohort;
  const isAuthor = getId(props.annotation.author) === userId.value;

  if (isCohortTeam && isAuthor) {
    const hasChildren = props.annotation.canUpdateOrDelete === false;
    return hasChildren;
  }

  const result =
    (props.disabled && props.annotation.nodes?.filter(n => n.nodeContent.trim().length).length) ||
    (getId(props.annotation.author) !== userId.value && props.parentCounterpoint.round === 1);

  return result;
});

const isGroupRoundUser = computed(() => {
  const isCohort = permissionLevel.value === PermissionLevel.COHORT;
  const publishedCheck = isCohort || props.parentCounterpoint.status !== RoundStatus.PUBLISHED;

  return (
    getId(props.annotation.author) === userId.value &&
    publishedCheck &&
    documentData.value.discussionType === 'curate'
  );
});

const isCohortTeam = computed(() => {
  const isCurrentUserCohort = permissionLevel.value === PermissionLevel.COHORT;

  const hasTeamMemberWithCohort = currentTeam.value?.members?.some(
    member =>
      (member.status === 'Accepted' || member.status === 'Invited') &&
      member.teamRole === PermissionLevel.COHORT
  );

  return isCurrentUserCohort || hasTeamMemberWithCohort;
});

const roundBorderColor = computed(() => {
  if (!props.vertical) return null;
  return props.annotation.roundCounterpoint?.round % 2 === 1 ? 'blue' : 'orange';
});

const verticalClasses = computed(() => {
  if (!props.vertical) return {};
  return {
    'vertical-annotation-box': true,
    'round-border-blue': roundBorderColor.value === 'blue',
    'round-border-orange': roundBorderColor.value === 'orange',
  };
});

const isCurrentAuthor = computed(() => {
  return getId(props.annotation.author) === userId.value;
});

const isCurrentRound = computed(() => {
  const isCurrentUserCohort = permissionLevel.value === PermissionLevel.COHORT;

  const hasTeamMemberWithCohort = currentTeam.value?.members?.some(
    member =>
      (member.status === 'Accepted' || member.status === 'Invited') &&
      member.teamRole === PermissionLevel.COHORT
  );

  const isCohortTeam = isCurrentUserCohort || hasTeamMemberWithCohort;

  const result =
    isHostInstructionMode.value ||
    isCohortTeam ||
    myLatestRoundNumber.value === props.parentCounterpoint.round;

  return result;
});

const isTeamLeadOrHostMode = computed(() => {
  return [PermissionLevel.TEAM_LEAD, PermissionLevel.HOST].includes(permissionLevel.value);
});

const isInstruction = computed(() => {
  return ['instruction', 'hostInstruction'].includes(props.annotation.type);
});

const isHostInstructionMode = computed(() => {
  return (
    props.annotation.type === 'hostInstruction' && permissionLevel.value === PermissionLevel.HOST
  );
});

const isAnnotationApproved = computed(() => props.annotation.approved > 0);
const isAnnotationDisapproved = computed(() => props.annotation.approved < 0);

const isMainAnnotationDisabled = computed(() => {
  const result = (props.disabled || !isCurrentAuthor.value) && !editingState.editMode.value;

  return result;
});

const authorName = computed(() => {
  if (props.annotation.editedBy?.name) {
    return props.annotation.editedBy.name;
  }
  if (props.annotation.author?.name) {
    return props.annotation.author.name.split(' ')[0];
  }
  return 'Unknown';
});

const canDeleteAnnotation = computed(() => {
  return isCurrentAuthor.value && props.annotation.canUpdateOrDelete !== false;
});

const annotationCitations = computed(() => {
  if (!citations.value[props.annotation._id]) return [];

  if (
    permissionLevel.value === PermissionLevel.COHORT &&
    !roundPublished.value(props.parentCounterpoint.round)
  ) {
    return citations.value[props.annotation._id].filter(
      citation =>
        citation.createdBy?._id === myLatestRound.value?.author?._id ||
        citation.createdBy?._id === userId.value
    );
  }

  return citations.value[props.annotation._id];
});

const hasCitations = computed(() => annotationCitations.value?.length > 0);

const canAddCitationsComputed = computed(() => {
  const withinCitationsLimit = annotationCitations.value?.length < 3;
  if (!withinCitationsLimit) return false;

  const isTeamLead = myLatestRound.value?.author?._id === userId.value;
  const isRoundFinalized = roundStatus.value === RoundStatus.FINALIZING;
  const isInstructionType = props.annotation.type === 'instruction';

  if (isTeamLead) {
    if (isCurrentAuthor.value || isRoundFinalized) return true;
  } else {
    const isAnnotationsSubmitted = teamMemberData.value?.submitted;
    if (isRoundFinalized || isAnnotationsSubmitted) return false;
    if (isCurrentAuthor.value || isInstructionType) return true;
  }

  return false;
});

const refAnnotations = computed(() => {
  return arrowsArray.value.filter(
    a => a.refAnnotation && a.refAnnotation._id === props.annotation._id
  );
});

const useInstruction = computed(() => {
  return (
    !roundPublished.value(props.parentCounterpoint.round) &&
    isInstruction.value &&
    !props.annotation.selectedReply &&
    props.annotation.comment?.replace(/<[/]?p>/gi, '').trim() === ''
  );
});

const showApprovalButtons = computed(() => {
  return (
    (permissionLevel.value === PermissionLevel.CURATOR &&
      props.annotation.roundCounterpoint?.round % 2 === 0 &&
      roundStatus.value === RoundStatus.FINALIZING &&
      props.annotation.type === 'comment' &&
      !isCurrentAuthor.value) ||
    (permissionLevel.value === PermissionLevel.TEAM_LEAD &&
      (roundStatus.value === RoundStatus.DRAFTING ||
        roundStatus.value === RoundStatus.FINALIZING) &&
      props.annotation.roundCounterpoint?.round === myLatestRoundNumber.value &&
      !isCurrentAuthor.value)
  );
});

const showFooter = computed(() => {
  return publicView.value || reportView.value || permissionLevel.value === PermissionLevel.OBSERVER;
});

const showTeamReplyInputBox = computed(() => {
  const isRoundActiveValue = roundActive.value(props.parentCounterpoint.round);
  const isMyLatestRound = myLatestRoundNumber.value === props.parentCounterpoint.round;
  const isTeamInstruction = permissionLevel.value === PermissionLevel.COHORT && isInstruction.value;
  const isHostInstruction =
    permissionLevel.value === PermissionLevel.TEAM_LEAD &&
    'hostInstruction' === props.annotation.type;
  return isRoundActiveValue && isMyLatestRound && (isTeamInstruction || isHostInstruction);
});

const showSaveButton = computed(() => {
  const isCurrentUserCohort = permissionLevel.value === PermissionLevel.COHORT;
  const hasTeamMemberWithCohort = currentTeam.value?.members?.some(
    member =>
      (member.status === 'Accepted' || member.status === 'Invited') &&
      member.teamRole === PermissionLevel.COHORT
  );
  const isCohortTeam = isCurrentUserCohort || hasTeamMemberWithCohort;

  const result =
    ((isCurrentRound.value &&
      (isCohortTeam || roundActive.value(props.parentCounterpoint.round)) &&
      isCurrentAuthor.value &&
      props.annotation.canUpdateOrDelete !== false) ||
      (isGroupRoundUser.value && props.annotation.canUpdateOrDelete !== false)) &&
    !isStaticMode.value;

  return result;
});

const showToolbar = computed(() => {
  const isCurrentUserCohort = permissionLevel.value === PermissionLevel.COHORT;
  const hasTeamMemberWithCohort = currentTeam.value?.members?.some(
    member =>
      (member.status === 'Accepted' || member.status === 'Invited') &&
      member.teamRole === PermissionLevel.COHORT
  );
  const isCohortTeam = isCurrentUserCohort || hasTeamMemberWithCohort;

  const canShowInPublishedRound = isCohortTeam && isCurrentAuthor.value;

  const result =
    ((!roundPublished.value(props.parentCounterpoint.round) || canShowInPublishedRound) &&
      isCurrentRound.value &&
      (hoverState.topBarHover.value || hasCitations.value)) ||
    showApprovalButtons.value ||
    isCurrentAuthor.value ||
    (!isCurrentAuthor.value && props.annotation.author);

  return result;
});

const tiptapStyle = computed(() => ({
  'max-height': hoverState.displayNone.value ? '100px' : 'unset',
  'overflow-y': 'hidden',
}));

const characterCountRemaining = computed(() => {
  return annotationCharacterLimit.value - teamAnnotationText.value.length;
});

const textBubbleClasses = computed(() => ({
  annotation: true,
  annotationBoxEllipsis: !props.vertical,
  ...verticalClasses.value,
  unapproved:
    permissionLevel.value === PermissionLevel.TEAM_LEAD &&
    !roundPublished.value(props.parentCounterpoint.round) &&
    props.annotation.type !== 'hostInstruction' &&
    !isCurrentAuthor.value &&
    (props.annotation.approved === undefined || props.annotation.approved === 0),
  approved:
    permissionLevel.value === PermissionLevel.TEAM_LEAD &&
    !roundPublished.value(props.parentCounterpoint.round) &&
    isAnnotationApproved.value,
  disapproved:
    permissionLevel.value === PermissionLevel.TEAM_LEAD &&
    !roundPublished.value(props.parentCounterpoint.round) &&
    isAnnotationDisapproved.value,
  replySelected:
    permissionLevel.value === PermissionLevel.TEAM_LEAD &&
    !roundPublished.value(props.parentCounterpoint.round) &&
    isInstruction.value &&
    props.annotation.teamReplies &&
    props.annotation.selectedReply,
  maxHeight: isStaticMode.value && !(hoverState.hover.value || hoverState.isFocused.value),
  minHeight: isStaticMode.value,
  'focus-active': hoverState.isFocused.value,
  'orange-on-edit': editingState.editMode.value,
  'red-on-unapproved':
    approvalState.isNotApproved.value && permissionLevel.value === PermissionLevel.CURATOR,
  'bump-effect': bumpEffect.value,
  'edit-error':
    selectedInstruction.value._id === props.annotation._id &&
    showCounterpointSelect.value &&
    characterCountRemaining.value < 0,
  'edit-succes':
    selectedInstruction.value._id === props.annotation._id &&
    showCounterpointSelect.value &&
    characterCountRemaining.value > 0,
  'uk-hidden':
    showCounterpointSelect.value && selectedInstruction.value._id !== props.annotation._id,
}));

// Helper computed properties for paragraphs
const inlineAnnotations = computed(() => {
  // Reference the cache to make it reactive
  const _ = cachedInlineNodeAnnotations.value;
  return inlineNodeAnnotations.value(props.annotation);
});

const excerptMaxChars = computed(() => {
  return 205;
});

const nodesForParagraph = computed(() => {
  if (!props.annotation.nodes) return [];

  return roundCount.value &&
    lastRoundNumber.value > 1 &&
    !hoverState.hover.value &&
    !hoverState.isFocused.value
    ? props.annotation.nodes.filter(
        n => ((n.offsets as any).endChar || n.offsets.end_char) < excerptMaxChars.value
      )
    : props.annotation.nodes;
});

const ellipsisAnnotations = computed(() => {
  if (!props.annotation.nodes) return {};

  const useEllipsis = nodesForParagraph.value.length < props.annotation.nodes.length;
  if (!useEllipsis) return {};

  const lastNode = nodesForParagraph.value[nodesForParagraph.value.length - 1];
  if (!lastNode) return {};

  const ellipsisText = '...';
  const start_char = lastNode.offsets.end_char;
  const end_char = lastNode.offsets.end_char + ellipsisText.length;

  const hiddenInlineAnnotations = Object.entries(inlineAnnotations.value).filter(
    ([k, v]: [string, any]) =>
      parseInt(k.split('-')[1]) >= excerptMaxChars.value && v.counterpointAnnotationIDs?.length
  );

  let hiddenInlineAnnotationsData = hiddenInlineAnnotations.reduce((obj: any, x: any) => {
    return {
      ...obj,
      counterpointAnnotationIDs: [
        ...(obj.counterpointAnnotationIDs || []),
        ...x[1].counterpointAnnotationIDs,
      ],
      counterpoints: [...(obj.counterpoints || []), ...x[1].counterpoints],
    };
  }, {});

  if (
    hiddenInlineAnnotationsData.counterpointAnnotationIDs &&
    hiddenInlineAnnotationsData.counterpoints
  ) {
    hiddenInlineAnnotationsData.counterpointAnnotationIDs = Array.from(
      new Set(hiddenInlineAnnotationsData.counterpointAnnotationIDs)
    );
    const allCounterpointIds = Array.from(
      new Set(hiddenInlineAnnotationsData.counterpoints.map((cp: any) => cp._id))
    );
    hiddenInlineAnnotationsData.counterpoints = allCounterpointIds.map(cpId =>
      hiddenInlineAnnotationsData.counterpoints.find((cp: any) => cp._id === cpId)
    );

    const nodeKey = `Node_${start_char}-${end_char}`;
    return {
      [nodeKey]: {
        counterpointAnnotationIDs: hiddenInlineAnnotationsData.counterpointAnnotationIDs,
        counterpoints: hiddenInlineAnnotationsData.counterpoints,
      },
    };
  }

  return {};
});

// Complex computed - annotation paragraphs
const annotationParagraphs = computed(() => {
  if (!props.annotation.nodes || !props.annotation.nodes.length) return [];

  const inlineAnnotationsValue = inlineAnnotations.value;
  const arrayEquals = (a: any[], b: any[]) => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  };

  let paragraphMapping = nodesForParagraph.value.reduce((paragraphs: any, node: any) => {
    return {
      ...paragraphs,
      [node.paragraphId]: paragraphs[node.paragraphId]
        ? [...paragraphs[node.paragraphId], node]
        : [node],
    };
  }, {});

  const paragraphs: any = {};
  for (const paragraphId in paragraphMapping) {
    const spans: any[] = [];

    if (counterpointLoaded.value) {
      let span: any = {};
      for (const node of paragraphMapping[paragraphId]) {
        if (!inlineAnnotationsValue[node.nodeKey]) continue;

        if (!span.counterpointAnnotationIDs) {
          span.counterpointAnnotationIDs =
            inlineAnnotationsValue[node.nodeKey].counterpointAnnotationIDs;
          span.sentenceId = node.sentenceId;
        }
        if (
          span.sentenceId != node.sentenceId ||
          !arrayEquals(
            span.counterpointAnnotationIDs,
            inlineAnnotationsValue[node.nodeKey].counterpointAnnotationIDs
          )
        ) {
          span.fullText = span.nodes.map((x: any) => x.nodeContent).join('');
          spans.push(span);
          span = {};
          span.counterpointAnnotationIDs =
            inlineAnnotationsValue[node.nodeKey].counterpointAnnotationIDs;
          span.sentenceId = node.sentenceId;
        }
        span.nodes = [...(span.nodes || []), node];
      }
      if (span.nodes) {
        span.fullText = span.nodes.map((x: any) => x.nodeContent).join('');
        spans.push(span);
      }
    }

    const allAnnotationIds = new Set();
    for (const span of spans) {
      for (const annotationId of span.counterpointAnnotationIDs) {
        allAnnotationIds.add(annotationId);
      }
    }

    paragraphs[paragraphId] = {
      paragraphId,
      nodes: paragraphMapping[paragraphId],
      fullText: paragraphMapping[paragraphId].map((node: any) => node.nodeContent).join(''),
      spans: spans,
      counterpointAnnotationIDs: Array.from(allAnnotationIds),
    };
  }

  const paragraphsArray = Object.values(paragraphs);

  // Add ellipsis if needed
  const useEllipsis = nodesForParagraph.value.length < props.annotation.nodes.length;
  if (useEllipsis && paragraphsArray.length > 0) {
    const lastParagraph: any = paragraphsArray[paragraphsArray.length - 1];
    if (lastParagraph.spans && lastParagraph.spans.length > 0) {
      const lastSpan = lastParagraph.spans[lastParagraph.spans.length - 1];
      const lastNode = lastSpan.nodes[lastSpan.nodes.length - 1];

      const hiddenNodes = props.annotation.nodes.filter(
        node => node.nodeOrder > lastSpan.nodes[lastSpan.nodes.length - 1].nodeOrder
      );
      const hiddenAnnotationIds = hiddenNodes.flatMap(
        node => inlineAnnotationsValue[node.nodeKey]?.counterpointAnnotationIDs || []
      );

      const ellipsisText = '...';
      const start_char = lastNode.offsets.end_char;
      const end_char = lastNode.offsets.end_char + ellipsisText.length;

      const ellipsisSpan = {
        counterpointAnnotationIDs: Array.from(new Set(hiddenAnnotationIds)),
        fullText: ellipsisText,
        nodes: [
          {
            nodeContent: ellipsisText,
            nodeKey: `Node_${start_char}-${end_char}`,
            nodeOrder: lastNode.nodeOrder + 1,
            nodeType: lastNode.nodeType,
            offsets: {
              start_char,
              end_char,
            },
            paragraphId: lastNode.paragraphId,
            sentenceId: lastNode.sentenceId,
          },
        ],
        sentenceId: lastNode.sentenceId,
      };

      lastParagraph.spans.push(ellipsisSpan);
    }
  }

  return paragraphsArray;
});

const paragraphNodeAnnotations = computed(() => {
  return {
    ...inlineAnnotations.value,
    ...ellipsisAnnotations.value,
  };
});

const textNodesMap = computed(() => {
  if (!props.annotation.nodes) return {};
  return props.annotation.nodes.reduce((obj: any, node: any) => {
    return {
      ...obj,
      [node.nodeKey]: node,
    };
  }, {});
});

// Methods
const handleInputKeyDown = () => {
  if (geometryState.doInputKeyDown()) {
    emit('resized');
  }
};

const handleClickAnnotation = (ev: Event) => {
  const target = ev.target as HTMLElement;
  const excludedClasses = [
    'custom-icon-height-margin citation-icon',
    'default-citaiton-icon',
    'citation-title',
    'citation uk-flex uk-flex-nowrap uk-flex-between uk-width-1-1 uk-position-relative',
    'uk-flex uk-flex-column uk-width-1-1 uk-text-small annotation-cite uk-background-muted',
  ];

  if (!excludedClasses.includes(target.classList.value) && target.classList.value !== '') {
    const proseMirror = document.querySelector(
      `#annotation-${props.annotation._id} .ProseMirror`
    ) as HTMLElement;
    proseMirror?.focus();
    emit('click', ev);
  }
};

const handleArrowClick = (data: { event: Event; annotation: any }) => {
  emit('click', data.event);
};

const handleHoverIn = (event: Event) => {
  const result = hoverState.hoverIn(
    event,
    useInstruction.value,
    editingState.activeFocus,
    editingState.previousText
  );
  emit('hoverIn', result.annotation, result.event);
};

const handleHoverOut = () => {
  hoverState.hoverOut();
  emit('hoverOut');
};

const handleFocusOut = async () => {
  await editingState.doFocusOut(showCounterpointSelect.value, isHostInstructionMode.value, {
    refByAnnotationsMap: refByAnnotationsMap.value,
  });
  emit('saved');
  EventBus.emit('UI.position.annotations', 0, 'temp');
  store.dispatch('setEditingAnnotationId', null);
};

const handleFocusIn = (useInstruction: boolean) => {
  editingState.doFocusIn(useInstruction);
  store.dispatch('setEditingAnnotationId', props.annotation._id);
};

const handleTeamReplyFocusIn = () => {
  editingState.doTeamReplyFocusIn();
};

const handleTeamReplyFocusOut = async () => {
  await editingState.doTeamReplyFocusOut();
  emit('saved');
  EventBus.emit('UI.position.annotations', 0, 'temp');
};

const doFocusOutAnn = () => {
  if (showTeamReplyInputBox.value) {
    handleTeamReplyFocusOut();
  } else {
    handleFocusOut();
  }
};

const handleGeometryUpdate = () => {
  const geometry = geometryState.setAnnotationGeometry();
  if (geometry) {
    emit('geometryUpdated', geometry);
  }
  EventBus.emit('UI.position.annotations', 0, 'temp');
  EventBus.emit('annotation.setup.complete', props.annotation?._id);
};

const handleGeometriesUpdated = (geometries: any) => {
  const updated = geometryState.updateParagraphGeometries(geometries);
  emit('geometriesUpdated', updated);
};

const showPreviousCitation = () => {
  previousCitation.value = !previousCitation.value;
  EventBus.emit('UI.position.annotations', 0, 'temp');
};

const handleAddCitation = () => {
  EventBus.emit('citation.new', props.annotation, 'annotation');
};

const handleDelete = () => {
  EventBus.emit('annotation.delete', props.annotation);
};

const doRepliesClick = () => {
  if (props.annotation.teamReplies?.length && permissionLevel.value !== PermissionLevel.COHORT) {
    store.commit('setSelectedInstruction', {
      _id: props.annotation._id,
      cpId: props.annotation.cpId,
    });
    store.commit('showTeamReplies');
    EventBus.emit('annotation.teamreplies', props.annotation);
    store.dispatch('setSelectedAnnotation', {
      counterpoint: props.parentCounterpoint,
      annotation: props.annotation,
    });
  }
};

const doMarkupClick = (node: any) => {
  const inlineAnnotations = inlineNodeAnnotations.value(props.annotation);
  if (inlineAnnotations[node.nodeKey]?.counterpointAnnotationIDs?.length) {
    emit('markupClick', node);
  }
};

async function showVerticalView() {
  activeAnnotations.value = [];
  setActiveAnnotations(props.annotation._id, myLatestRoundNumber.value - 1);
  store.commit('setVerticalView', true);
  await UIkit.modal(document.getElementById('verticalViewModal')).show();
  setTimeout(() => {
    dragElement(document.getElementsByClassName('vertical-modal')[0], 'modalHeader');
  }, 500);
}

function setActiveAnnotations(id: string, index: number) {
  counterpointsFlat.value?.map((data: any) => {
    if (data._id === id) {
      if (data.refAnnotation?._id) {
        activeAnnotations.value[index] = id;
        setActiveAnnotations(data.refAnnotation._id, index - 1);
      } else {
        activeAnnotations.value[index] = id;
        store.commit('setVerticalViewId', activeAnnotations.value);
      }
    }
  });
}

async function submitTeamReplies() {
  EventBus.emit('selection.modal.close');
  await store.dispatch('updateAnnotation', {
    annotation: props.annotation,
    properties: { comment: teamAnnotationText.value },
  });

  for (let i = 0; i < props.annotation.teamReplies.length; i++) {
    const reply = props.annotation.teamReplies[i];
    let status = reply.status || 'unchanged';
    if (
      teamAnnotationText.value.includes(reply.comment?.replace(/<[/]?p>/gi, '').trim()) &&
      reply.status === 'accepted'
    ) {
      status = 'accepted as it is';
    }
    await xhr.updateTeamReply(reply._id, { status });
  }
}

// Watchers
watch(
  () => editingState.annotationText.value,
  value => {
    emit('mainTextChanged', value);
  }
);

watch(
  () => props.annotation.comment,
  value => {
    if (value) editingState.annotationText.value = value;
  },
  { immediate: true }
);

watch(
  () => props.annotation.instruction,
  value => {
    if (value) editingState.annotationText.value = value;
  },
  { immediate: true }
);

watch(
  () => props.annotation._id,
  (newValue, oldValue) => {
    if (
      oldValue &&
      !editingState.activeFocus.value &&
      newValue !== oldValue &&
      isCurrentRound.value &&
      (oldValue.includes('temp') || newValue.includes('temp'))
    ) {
      doFocusOutAnn();
    }

    if (!newValue.includes('temp')) {
      citationDis.value = false;
    }
  },
  { immediate: true }
);

watch(
  () => annotationFocusIds.value[props.annotation._id],
  newValue => {
    if (newValue && !hoverState.isFocused.value) {
      hoverState.isFocused.value = true;
    } else if (!newValue && hoverState.isFocused.value) {
      hoverState.isFocused.value = false;
    }
  }
);

watch(annotationActive, (value, oldValue) => {
  if (props.annotation._id === oldValue) {
    hoverState.hoverOut();
  }
});

const citationSaved = () => {
  if (instance) {
    instance.proxy.$forceUpdate();
  }
};

const citationRemoved = () => {
  if (instance) {
    instance.proxy.$forceUpdate();
  }
};

const handleDelayedGeometryUpdate = () => {
  handleGeometryUpdate();
};

// Lifecycle hooks
onMounted(() => {
  geometryState.annotationBoxEllipsisSize();

  // Init annotation text
  editingState.annotationText.value = useInstruction.value
    ? props.annotation.instruction || ''
    : props.annotation.comment;

  // Init team reply text
  if (props.annotation.teamReplies) {
    const reply = props.annotation.teamReplies.find(r => getId(r.author) === userId.value);
    if (reply) {
      editingState.teamReplyComment.value = reply.comment;
    }
  }

  // Setup annotation geometry
  geometryState.setupAnnotationGeometry();

  // Setup event bus listeners
  window.addEventListener('resize', geometryState.annotationBoxEllipsisSize);
  EventBus.on('annotation.save.status', editingState.setAnnotationSaving);
  EventBus.on('citation.saved', citationSaved);
  EventBus.on('citation.removed', citationRemoved);
  EventBus.on('UI.position.annotations.delayed', handleDelayedGeometryUpdate);

  if (props.annotation.canUpdateOrDelete === false) {
    canUpdateAndDelete.value = false;
  }
});

onBeforeUnmount(() => {
  // Remove event bus listeners
  window.removeEventListener('resize', geometryState.annotationBoxEllipsisSize);
  EventBus.off('annotation.save.status', editingState.setAnnotationSaving);
  EventBus.off('citation.saved', citationSaved);
  EventBus.off('citation.removed', citationRemoved);
  EventBus.off('UI.position.annotations.delayed', handleDelayedGeometryUpdate);
});

// Expose methods for parent components
defineExpose({
  setAnnotationGeometry: handleGeometryUpdate,
  annotationParagraphs,
  textNodesMap,
  annotation: props.annotation,
});
</script>

<style scoped lang="scss">
.tip-tap-overflow-ellipsis {
  overflow: hidden;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  margin-bottom: 8px;
}

.citation-icon-dummy {
  position: absolute;
  top: 15px;
  right: 15px;
}

.annotation {
  margin-top: 1rem;

  &-item {
    width: 100%;
    transition: top 0.5s ease;
  }
}

.padding-action {
  padding: 5px 0;
}

.display-none {
  display: none;
}

.size-btn {
  height: 40px;
  width: 60px;
}

.bump-effect {
  animation-name: example;
  animation-duration: 1s;

  @keyframes example {
    0% {
      transform: scale(0.95);
    }
    50% {
      transform: scale(1.05);
    }
    100% {
      transform: scale(1);
    }
  }
}

.open-citation-style {
  margin-top: 2px;
}

.vertical-annotation-box {
  margin-top: 0px !important;

  .text-bubble {
    transform: translateY(0px) !important;
  }
}

.annotation {
  :deep(*) {
    font-size: $global-font-size;
  }
}
</style>

<style lang="scss">
.round-border-blue > div {
  border: 2px solid #7cb6c9 !important;
}

.round-border-orange > div {
  border: 2px solid #e49141 !important;
}

.orange-on-edit > div {
  border-color: #faa05a !important;
}

.red-on-unapproved > div {
  border-color: red !important;
}

.edit-succes > div {
  border-color: #00bb3a !important;
}

.edit-error > div {
  border-color: #f0506e !important;
}
</style>
